<template>
  <nav v-if="!hideBreadcrumbs || isCRM" class="breadcrumbs" aria-label="breadcrumbs">
    <div class="breadcrumbs-container">
      <svg v-if="showIcon" @click="backIconOnClick">
        <use xlink:href="/assets/public/images/icons.svg#arrow" />
      </svg>
      <ol v-if="!isCRM">
        <template v-if="currentDevice != DeviceTypes.MOBILE">
          <li v-for="(crumb, i) in crumbs" :key="`crumb level ${i} - ${crumb.text}`">
            <router-link v-if="crumb.to" :to="crumb.to">{{ crumb.text }}</router-link>
            <a v-else :aria-current="i === crumbs.length - 1 ? 'location' : null" href="#">{{ crumb.text }}</a>
          </li>
        </template>
        <template v-else>
          <li>
            <router-link v-if="previousStepDetails?.to" :to="previousStepDetails.to">{{ previousStepDetails.text }}</router-link>
            <a v-else :aria-current="previousStepDetails?.index" href="#">{{ previousStepDetails?.text }}</a>
          </li>
        </template>
      </ol>
      <ol v-if="isCRM" class="crm-breadcrumbs">
        <li v-for="(crumb, i) in CRMBreadcrumbs" :key="`crumb level ${i} - ${crumb.text}`">
          <button v-if="crumb.navigate" class="non-button-style" @click="crmNavigatePage(crumb.navigate)">{{ crumb.text }}</button>
          <a v-else :aria-current="i === CRMBreadcrumbs.length - 1 || crumb.disabled ? 'location' : null">{{ crumb.text }}</a>
        </li>
      </ol>
    </div>
  </nav>
</template>

<script>
import { mapGetters } from 'vuex';
import { HOME, NO_IMPACT_UPDATE_DETAILS, PREMIUM_IMPACT_UPDATE_DETAILS } from '@/router/routeNames';
import { getCurrentDeviceLayout } from '@/utils';
import DeviceTypes from '@/enums/DeviceTypes';

export default {
  name: 'Breadcrumb',

  props: {
    /**
     * By default is false. if false, it means this component is used for member portal.
     * if true, it is used for CRM vue standalone page
     */
    isCRM: {
      type: Boolean,
      default: false,
    },

    /**
     * this should be required if `isCRM` prop is true
     * it should be array of object, where object should have following:
     * - text: text to be used for crumb
     * - navigate: function that is passed to navigate between page within CRM standalone vue app
     */
    CRMBreadcrumbs: {
      type: Array,
    },

    /**
     * This prop determines whether the back icon will be displayed to the left of all the links.
     * False by default, if clicked takes the member back one step.
     */
    includeIcon: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      HOME,
      currentDevice: DeviceTypes.DESKTOP,
      DeviceTypes,
    };
  },

  beforeMount() {
    // initialism current device layout
    this.resizeHandler();
  },

  mounted() {
    window.addEventListener('resize', this.resizeHandler);
  },

  unmounted() {
    window.removeEventListener('resize', this.resizeHandler);
  },

  computed: {
    ...mapGetters({
      policyNumber: 'getCurrentPolicyNumber',
    }),

    hideBreadcrumbs() {
      return this.crumbs.length === 1 || window.location.pathname.includes('/error');
    },

    crumbs() {
      if (this.isCRM) return [];

      const pathArray = this.$route.path.split('/').filter((value) => {
        return value !== '';
      });

      const breadcrumbs = pathArray.map((path, index) => {
        const thisPath = pathArray.slice(0, index + 1).join('/');
        const meta = this.$route.matched[index].meta;
        const isCategory = this.$route.matched[index].meta.isCategory;
        let title = meta.title;

        /**
         * set title meta data for those routes with dynamic title meta data
         *
         * NOTE: depend on what the child route of this route, this route's title, which is dynamically generated, string data may come different source.
         *
         * eg: most likely param of this route would be used to dynamically generate its title meta data. however, if member is accessing to its child route directly by typing url, technically, because of member has not entered this route, no params has passed hence title meta data that is generated based on passed param data would not be generated. but since its child url contains url params to generate this route's title hence in this case, title should be assigned to some of params data of child route (look at the in case of next route name is no impact on the below code)
         */
        if (meta.dynamicTitle && title === undefined && index < pathArray.length - 1) {
          const nextRouteName = this.$route.matched[index + 1].name;
          switch (nextRouteName) {
            case NO_IMPACT_UPDATE_DETAILS:
              title = this.$route.params.name;
              break;
            case PREMIUM_IMPACT_UPDATE_DETAILS:
              title = this.$route.params.name;
              break;
            default:
              break;
          }
        }

        if (title === undefined) {
          return false;
        }

        // always home
        if (index === 0) {
          return {
            to: {
              name: HOME,
              params: { policyNumber: this.policyNumber },
            },
            text: HOME,
          };
        }

        if (index !== pathArray.length - 1 && index >= 1 && !isCategory) {
          return {
            to: `/${thisPath}`,
            text: title,
          };
        }

        return {
          text: title,
        };
      });

      return breadcrumbs.filter(Boolean);
    },

    showIcon() {
      return this.includeIcon && this.crumbs.length > 1;
    },
    previousStepDetails() {
      // Get index of penultimate item
      const startingIndex = this.crumbs.length - 2;

      // Check there is valid page link from penultimate to first item in breadcrumb
      for (let i = startingIndex; i >= 0; i--) {
        if (this.crumbs[i].to) {
          return { ...this.crumbs[i], index: i };
        }
      }

      return null;
    },
  },

  methods: {
    /**
     * emit data to parent for page navigation in CRM.
     *
     * @param {string} targetPageName name of target page. it should be one of `PAGES` object value.
     * @param {object} routeParams by default is empty object. if given, it should be object. this object will be used like route param in non CRM build.
     */
    crmNavigatePage(targetPageName) {
      window.scrollTo(0, 0);
      this.$emit('crmNavigatePage', targetPageName);
    },
    // Gets the last viewable page in breadcrumb
    getPreviousStepLink() {
      // Get index of penultimate item
      const startingIndex = this.crumbs.length - 2;

      // Check there is valid page link from penultimate to first item in breadcrumb
      for (let i = startingIndex; i >= 0; i--) {
        if (this.crumbs[i].to) {
          return this.crumbs[i].to;
        }
      }
      // If there is no valid prev step link
      return '#';
    },
    backIconOnClick() {
      // Re-direct user to previous page in breadcrumb trail
      const previousStep = this.getPreviousStepLink();
      this.$router.push(previousStep);
    },
    resizeHandler() {
      // This is to fetch the current device layout so that appropriate version of my recommendation is shown
      this.currentDevice = getCurrentDeviceLayout(window.innerWidth);
    },
  },
};
</script>

<style lang="scss" scoped>
ol.crm-breadcrumbs {
  button.non-button-style {
    text-decoration: none;

    &:hover {
      text-decoration: underline;
      color: $CRIMSON;
      font-weight: $BOLD;
    }
  }

  li:last-child {
    text-decoration: none;

    a:hover {
      text-decoration: unset;
      color: unset;
    }
  }
}
</style>